// THE ONE MAP OF THE DASHBOARD (nav v2): section → tab → view, and which existing route each one renders.
//
// Sections are the slim rail. A section's tabs are the horizontal tabs in its sticky header. When a tab has more
// than one existing screen behind it, those are its VIEWS and the shell draws them as a segmented switch, never as
// more navigation. The information architecture is the approved dashboard map (scratchpad dashboard-map, SECTIONS);
// a tab with no screen yet is left out rather than drawn as a placeholder.
//
// Two directions, both here so they cannot drift apart:
//   resolveRoute(pathname, search)  where the current address sits: rail highlight, active tab, active view, and
//                                   whether the address names a site (or means "All sites").
//   sectionTabs(section, site)      what the header shows for a scope: the tabs and views that have a screen, with
//                                   their hrefs.
//
// Every route in App.tsx resolves (test/nav-v2/map.test.mjs reads App.tsx and checks), so an old URL always lights
// the right place. Nothing here imports React or the router, so the map runs under plain node.

export type SectionId = "home" | "content" | "review" | "analytics" | "leads" | "settings" | "connections" | "more";

/** The fields of a website the map reads. `WebsiteRecord` (GET /v1/websites) satisfies it. */
export interface NavSite {
  ref: string;
  name: string;
  domain?: string | null;
  xano_site_id?: number;
  journey_id?: string;
  task_site_ref?: string;
  analytics_site_id?: string;
  replay_site_id?: string;
  journey_site_id?: string;
  /**
   * What applies to this site (WebsiteRecord.capabilities). A view that `needs` one of them is left out when the site
   * has none of those; a site whose capabilities are not known yet (the address's id standing in while the list loads)
   * keeps every view, so nothing flickers away and back.
   */
  capabilities?: readonly string[];
}

/** Build flags that switch a view on (the same env flags the classic sidebar reads). */
export interface NavEnv { scheduling?: boolean; businesses?: boolean }

interface ViewDef {
  id: string;
  label: string;
  /** The same screen whatever the scope (not site-aware). */
  any?: string;
  /** The All-sites screen. */
  all?: string;
  /** The one-site screen, or null when this site has none (a site made in Bloxx has no Xano page list). */
  site?: (w: NavSite) => string | null;
  env?: keyof NavEnv;
  /** For one site: shown only when the site has at least one of these capabilities. */
  needs?: readonly string[];
}
interface TabDef { id: string; label: string; views: ViewDef[]; hint?: string }
export interface SectionDef { id: SectionId; label: string; place: "main" | "foot"; tabs: TabDef[]; keep?: string[] }

const enc = encodeURIComponent;
/**
 * Website-only views (pages, editor, keywords, reviews, SEO and traffic reports, leads, approvals): a website has
 * "pages" (a Xano site) or "editor" (built in Bloxx). An app or tool (WebsiteKind "app") has neither, so its scope
 * shows Overview, Tasks, People and Settings, and Analytics only once Google is linked to it ("analytics", "search").
 */
const WEBSITE = ["pages", "editor"] as const;
export const sitePath = (w: Pick<NavSite, "ref">) => `/sites/${enc(w.ref)}`;
const siteTab = (tab: string) => (w: NavSite) => `${sitePath(w)}?tab=${tab}`;
const xanoOnly = (path: (id: number) => string) => (w: NavSite) => (w.xano_site_id != null ? path(w.xano_site_id) : null);
const analytics = (tab: string) => ({
  all: tab === "overview" ? "/analytics" : `/analytics?tab=${tab}`,
  site: (w: NavSite) => `/analytics?${tab === "overview" ? "" : `tab=${tab}&`}site=${enc(w.ref)}`,
});

export const SECTIONS: SectionDef[] = [
  { id: "home", label: "Home", place: "main", tabs: [
    { id: "overview", label: "Overview", views: [
      { id: "sites", label: "Sites", all: "/sites", site: (w) => sitePath(w) },
      { id: "summary", label: "Summary", all: "/overview" },
    ] },
    { id: "health", label: "Health", views: [{ id: "health", label: "Health", all: "/insights/audit", site: siteTab("audit"), needs: WEBSITE }] },
  ] },
  { id: "content", label: "Content", place: "main", tabs: [
    // The CMS list (routes/PagesAll): every store in one table, the scope as its site filter. One site is the same list
    // scoped to it (/pages?site=<ref>), for a Xano site too, so a bloxx.page copy attributed to it is listed there.
    // The older per-site list (/sites/:id/pages) still resolves here for old links.
    { id: "pages", label: "Pages", views: [{ id: "pages", label: "Pages", all: "/pages", site: (w) => `/pages?site=${enc(w.ref)}`, needs: WEBSITE }] },
    // CMS collections of a website made in Bloxx (features/collections): table rows as pages, reviewed and published.
    { id: "collections", label: "Collections", views: [{ id: "collections", label: "Collections", site: (w) => (w.journey_id ? siteTab("collections")(w) : null), needs: WEBSITE }] },
    // Keyword ↔ page mapping (routes/PagesKeywords): a website's keyword list against its pages, all websites as counts.
    { id: "keywords", label: "Keywords", views: [{ id: "keywords", label: "Keywords", all: "/pages/keywords", site: (w) => `/pages/keywords?site=${enc(w.ref)}`, needs: WEBSITE }] },
    // Ratings and reviews from Trustpilot and Google (routes/ContentReviews), and the pages that show a rating. All
    // websites: one card per website. Connecting a source is Connections' job; this tab links there.
    { id: "reviews", label: "Reviews", views: [{ id: "reviews", label: "Reviews", all: "/pages/reviews", site: (w) => `/pages/reviews?site=${enc(w.ref)}`, needs: WEBSITE }] },
    // A site made in Bloxx opens its full-screen editor directly (no stop at /sites/:ref?tab=edit, which only redirects
    // there); an older Xano site keeps its page editor on the site page.
    { id: "editor", label: "Editor", views: [{ id: "editor", label: "Editor", site: (w) => (w.journey_id ? `${sitePath(w)}/editor` : siteTab("edit")(w)), needs: WEBSITE }] },
  ] },
  { id: "review", label: "Review", place: "main", tabs: [
    { id: "approvals", label: "Approvals", hint: "Available after moving to the new editor: page changes go to an approver, with an audit trail.", views: [{ id: "approvals", label: "Approvals", site: siteTab("review"), needs: WEBSITE }] },
    { id: "tasks", label: "Tasks", views: [{ id: "tasks", label: "Tasks", all: "/tasks", site: siteTab("tasks") }] },
    { id: "activity", label: "Activity", hint: "Available after moving to the new editor: who changed what, with restore.", views: [{ id: "activity", label: "Activity", site: siteTab("activity"), needs: WEBSITE }] },
  ] },
  { id: "analytics", label: "Analytics", place: "main", keep: ["range"], tabs: [
    { id: "overview", label: "Overview", views: [{ id: "overview", label: "Overview", ...analytics("overview"), needs: [...WEBSITE, "analytics"] }] },
    { id: "search", label: "Search", views: [{ id: "search", label: "Search", ...analytics("search"), needs: [...WEBSITE, "search"] }] },
    { id: "traffic", label: "Traffic", views: [{ id: "traffic", label: "Traffic", ...analytics("traffic"), needs: WEBSITE }] },
    { id: "content", label: "Pages & content", views: [{ id: "content", label: "Pages & content", ...analytics("content"), needs: WEBSITE }] },
    { id: "ai", label: "AI visibility", views: [{ id: "ai", label: "AI visibility", ...analytics("ai"), needs: WEBSITE }] },
    { id: "recordings", label: "Recordings", views: [{ id: "recordings", label: "Recordings", ...analytics("recordings"), needs: WEBSITE }] },
    { id: "experiments", label: "Experiments", views: [{ id: "experiments", label: "Experiments", ...analytics("experiments"), needs: WEBSITE }] },
  ] },
  { id: "leads", label: "Leads", place: "main", tabs: [
    { id: "inbox", label: "Inbox", views: [{ id: "inbox", label: "Inbox", all: "/submissions", site: (w) => `/submissions?site=${enc(w.ref)}`, needs: WEBSITE }] },
  ] },
  { id: "settings", label: "Settings", place: "main", tabs: [
    { id: "site", label: "Site", views: [{ id: "site", label: "Site", site: siteTab("settings") }] },
    { id: "integrations", label: "Integrations", views: [
      { id: "setup", label: "Setup", all: "/all-sites" },
      { id: "mapping", label: "Property mapping", all: "/integrations/classic" },
      { id: "tracking", label: "Tracking & tools", site: xanoOnly((id) => `/sites/${id}/tracking`), needs: WEBSITE },
      { id: "goals", label: "Goals", site: xanoOnly((id) => `/sites/${id}/goals`), needs: WEBSITE },
    ] },
    { id: "handbook", label: "Handbook", views: [{ id: "handbook", label: "Handbook", site: (w) => (w.journey_id ? siteTab("handbook")(w) : null) }] },
    { id: "people", label: "People", views: [{ id: "people", label: "People", site: siteTab("people") }] },
    { id: "workspace", label: "Workspace", views: [
      { id: "projects", label: "Projects", any: "/projects" },
      { id: "billing", label: "Plan & billing", any: "/billing" },
      { id: "domains", label: "Domain registrant", any: "/settings/domains" },
      { id: "security", label: "Security", any: "/settings/security" },
      { id: "businesses", label: "Businesses", any: "/businesses", env: "businesses" },
    ] },
  ] },
  { id: "connections", label: "Connections", place: "foot", tabs: [
    // Per website for the services a website brings its own key for (Trustpilot, SEMrush); Google stays account-wide.
    { id: "accounts", label: "Accounts", views: [{ id: "accounts", label: "Accounts", all: "/connections", site: (w) => `/connections?site=${enc(w.ref)}` }] },
    { id: "apps", label: "Apps & assistants", views: [{ id: "apps", label: "Apps & assistants", any: "/connected-apps" }] },
  ] },
  { id: "more", label: "More tools", place: "foot", tabs: [
    { id: "data", label: "Data", views: [
      { id: "tables", label: "Tables", any: "/tables" },
      { id: "interfaces", label: "Interfaces", any: "/interfaces" },
      { id: "links", label: "Links", any: "/links" },
      { id: "feeds", label: "Feeds", any: "/feeds" },
    ] },
    { id: "media", label: "Media", views: [
      { id: "brands", label: "Brands", any: "/media/brands" },
      { id: "create", label: "Create", any: "/media/create" },
      { id: "library", label: "Library", any: "/media/library" },
      { id: "studio", label: "Studio", any: "/studio" },
      { id: "guides", label: "Guides", any: "/guides" },
    ] },
    { id: "social", label: "Social", views: [
      { id: "publishing", label: "Publishing", any: "/social" },
      { id: "review", label: "Review queue", any: "/content" },
      { id: "operations", label: "Operations", any: "/social/operations" },
    ] },
    { id: "work", label: "Documents", views: [{ id: "work", label: "Documents", any: "/work" }] },
    { id: "fixes", label: "Fixes", views: [
      { id: "fixes", label: "Fixes", any: "/fixes" },
      { id: "schema", label: "Schema backfill", any: "/tools/schema-backfill" },
    ] },
    { id: "scheduling", label: "Scheduling", views: [{ id: "scheduling", label: "Scheduling", any: "/scheduling", env: "scheduling" }] },
  ] },
];

/* ───────────────────────────── where an address sits ───────────────────────────── */

/** How an address names its scope: it means All sites, or the site is in a path param or a query param. */
/** `optional`: without the query param the address says nothing, so the chosen site stays (Connections). */
type ScopeRule = "all" | { param: string } | { query: string; optional?: true };
interface Rule {
  path: string;
  /** Query conditions: a value must equal one of these (null: the param must be absent). */
  when?: Record<string, (string | null)[]>;
  at: [SectionId, string, string?];
  scope?: ScopeRule;
}

const ANALYTICS_TABS = ["search", "traffic", "content", "ai", "recordings", "experiments"] as const;
const SITE_TABS: [string, [SectionId, string]][] = [
  ["audit", ["home", "health"]], ["edit", ["content", "editor"]], ["review", ["review", "approvals"]], ["collections", ["content", "collections"]], ["tasks", ["review", "tasks"]],
  ["activity", ["review", "activity"]], ["analytics", ["analytics", "overview"]], ["handbook", ["settings", "handbook"]],
  ["people", ["settings", "people"]], ["settings", ["settings", "site"]],
];
const SITE: ScopeRule = { param: "siteId" };
const Q_SITE: ScopeRule = { query: "site" };

/** First match wins, so the fixed paths come before the ones with a param in the same place. */
export const RULES: Rule[] = [
  // Home
  { path: "/", at: ["home", "overview", "sites"], scope: "all" },
  ...["/sites", "/sites/legacy", "/sites/new", "/sites/connect", "/ideas", "/ideas/batch", "/ideas/parent", "/ideas/compare"].map((path): Rule => ({ path, at: ["home", "overview", "sites"], scope: "all" })),
  { path: "/overview", at: ["home", "overview", "summary"], scope: "all" },
  { path: "/ideas/:id", at: ["home", "overview", "sites"], scope: { param: "id" } },
  { path: "/ideas/:id/workspace", at: ["content", "editor", "editor"], scope: { param: "id" } },
  { path: "/insights/audit", at: ["home", "health", "health"], scope: "all" },
  { path: "/insights/audit/:siteId", at: ["home", "health", "health"], scope: "all" },
  // The site page: its ?tab= is a tab of a section, so the page and the header are one system.
  ...SITE_TABS.map(([tab, [section, id]]): Rule => ({ path: "/sites/:siteId", when: { tab: [tab] }, at: [section, id], scope: SITE })),
  { path: "/sites/:siteId", at: ["home", "overview", "sites"], scope: SITE },
  // Content
  { path: "/pages", at: ["content", "pages", "pages"], scope: Q_SITE },
  { path: "/pages/keywords", at: ["content", "keywords", "keywords"], scope: Q_SITE },
  { path: "/pages/reviews", at: ["content", "reviews", "reviews"], scope: Q_SITE },
  { path: "/sites/:siteId/pages", at: ["content", "pages", "pages"], scope: SITE },
  { path: "/sites/:siteId/pages/import", at: ["content", "pages", "pages"], scope: SITE },
  { path: "/sites/:siteId/pages/:pageId/edit", at: ["content", "editor", "editor"], scope: SITE },
  { path: "/sites/:siteId/editor", at: ["content", "editor", "editor"], scope: SITE },
  // Review
  { path: "/sites/:siteId/review", at: ["review", "approvals", "approvals"], scope: SITE },
  { path: "/tasks", at: ["review", "tasks", "tasks"], scope: Q_SITE },
  // Analytics: the home's ?tab=, then the old ?view= names (Traffic now), then its overview.
  ...ANALYTICS_TABS.map((tab): Rule => ({ path: "/analytics", when: { tab: [tab] }, at: ["analytics", tab, tab], scope: Q_SITE })),
  { path: "/analytics", when: { tab: [null], view: ["overview", "journeys", "pages", "requests"] }, at: ["analytics", "traffic", "traffic"], scope: Q_SITE },
  { path: "/analytics", at: ["analytics", "overview", "overview"], scope: Q_SITE },
  { path: "/insights/sessions/:siteId/:sessionId", at: ["analytics", "recordings", "recordings"], scope: SITE },
  { path: "/insights/recording/:siteId/install", at: ["analytics", "recordings", "recordings"], scope: SITE },
  { path: "/insights/replay/:siteId/:sessionId", at: ["analytics", "recordings", "recordings"], scope: SITE },
  ...["/insights/sessions", "/insights/journeys", "/session-replay"].map((path): Rule => ({ path, at: ["analytics", "recordings", "recordings"], scope: Q_SITE })),
  { path: "/experiments", at: ["analytics", "experiments", "experiments"], scope: Q_SITE },
  { path: "/ai-visibility", at: ["analytics", "ai", "ai"], scope: Q_SITE },
  { path: "/keywords", at: ["analytics", "search", "search"] },
  { path: "/keywords/research", at: ["analytics", "search", "search"] },
  { path: "/content-performance", at: ["analytics", "content", "content"] },
  { path: "/content-performance/:contentId", at: ["analytics", "content", "content"] },
  // Every other /insights address redirects into the Analytics home (features/analytics-home/redirects.tsx).
  { path: "/insights", at: ["analytics", "overview", "overview"] },
  { path: "/insights/:tab", at: ["analytics", "overview", "overview"] },
  { path: "/insights/:tab/:siteId", at: ["analytics", "overview", "overview"] },
  // Leads
  { path: "/submissions", at: ["leads", "inbox", "inbox"], scope: Q_SITE },
  // Settings
  { path: "/sites/:siteId/tracking", at: ["settings", "integrations", "tracking"], scope: SITE },
  { path: "/sites/:siteId/goals", at: ["settings", "integrations", "goals"], scope: SITE },
  { path: "/all-sites", at: ["settings", "integrations", "setup"], scope: "all" },
  { path: "/integrations/classic", at: ["settings", "integrations", "mapping"], scope: "all" },
  { path: "/projects", at: ["settings", "workspace", "projects"] },
  { path: "/projects/:projectId", at: ["settings", "workspace", "projects"] },
  { path: "/billing", at: ["settings", "workspace", "billing"] },
  { path: "/settings/domains", at: ["settings", "workspace", "domains"] },
  { path: "/settings/security", at: ["settings", "workspace", "security"] },
  { path: "/businesses", at: ["settings", "workspace", "businesses"] },
  // Connections
  { path: "/connections", at: ["connections", "accounts", "accounts"], scope: { query: "site", optional: true } },
  { path: "/integrations", at: ["connections", "accounts", "accounts"] },
  { path: "/integrations/google/callback", at: ["connections", "accounts", "accounts"] },
  { path: "/integrations/social/callback", at: ["connections", "accounts", "accounts"] },
  { path: "/connected-apps", at: ["connections", "apps", "apps"] },
  // More tools
  ...["/tables", "/tables/:workspaceId", "/tables/:workspaceId/:tableId"].map((path): Rule => ({ path, at: ["more", "data", "tables"] })),
  { path: "/interfaces", at: ["more", "data", "interfaces"] },
  { path: "/links", at: ["more", "data", "links"] },
  { path: "/feeds", at: ["more", "data", "feeds"] },
  ...["/media/brands", "/media/brands/:workspaceId/:brandId"].map((path): Rule => ({ path, at: ["more", "media", "brands"] })),
  { path: "/media/create", at: ["more", "media", "create"] },
  { path: "/media/library", at: ["more", "media", "library"] },
  ...["/studio", "/studio/:workspaceId/:recordingId"].map((path): Rule => ({ path, at: ["more", "media", "studio"] })),
  ...["/guides", "/guides/:workspaceId/:captureId"].map((path): Rule => ({ path, at: ["more", "media", "guides"] })),
  { path: "/social/operations", at: ["more", "social", "operations"] },
  ...["/social", "/social/:contentId", "/social/handoff/:handoffId"].map((path): Rule => ({ path, at: ["more", "social", "publishing"] })),
  { path: "/content", at: ["more", "social", "review"] },
  ...["/work", "/work/:workspaceId/:documentId"].map((path): Rule => ({ path, at: ["more", "work", "work"] })),
  { path: "/fixes", at: ["more", "fixes", "fixes"] },
  { path: "/tools/schema-backfill", at: ["more", "fixes", "schema"] },
  { path: "/scheduling", at: ["more", "scheduling", "scheduling"] },
];

/** A route pattern against a pathname: `:name` takes one segment. Trailing slashes are ignored. */
export function matchPattern(pattern: string, pathname: string): Record<string, string> | null {
  const split = (p: string) => p.split("/").filter(Boolean);
  const want = split(pattern), got = split(pathname);
  if (want.length !== got.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < want.length; i++) {
    const w = want[i]!, g = got[i]!;
    if (w.startsWith(":")) {
      try { params[w.slice(1)] = decodeURIComponent(g); } catch { params[w.slice(1)] = g; }
    } else if (w !== g) return null;
  }
  return params;
}

export interface Resolved {
  section: SectionId;
  tab: string;
  view?: string;
  /** Absent: this address does not say which site (the shell keeps the chosen one). "all": All sites. */
  scope?: "all" | { raw: string };
}

export function resolveRoute(pathname: string, search = ""): Resolved | null {
  const q = new URLSearchParams(search);
  for (const rule of RULES) {
    const params = matchPattern(rule.path, pathname);
    if (!params) continue;
    if (rule.when && !Object.entries(rule.when).every(([k, ok]) => ok.includes(q.get(k)))) continue;
    const [section, tab, view] = rule.at;
    let scope: Resolved["scope"];
    if (rule.scope === "all") scope = "all";
    else if (rule.scope && "param" in rule.scope) scope = params[rule.scope.param] ? { raw: params[rule.scope.param]! } : "all";
    else if (rule.scope && "query" in rule.scope) {
      const raw = q.get(rule.scope.query);
      scope = raw && raw !== "all" ? { raw } : raw === null && rule.scope.optional ? undefined : "all";
    }
    return { section, tab, ...(view ? { view } : {}), ...(scope ? { scope } : {}) };
  }
  return null;
}

/** Which website an address's id means: its ref, Xano id (or xano:N, legacy:site:N), journey id (or journey:J), task ref, builder site id or host. */
export function findSite<T extends NavSite>(list: readonly T[], raw: string | null | undefined): T | null {
  if (!raw) return null;
  const id = raw.trim();
  const lower = id.toLowerCase();
  const n = /^(?:xano:|legacy:site:|website-)?(\d+)$/.exec(id)?.[1];
  const journey = id.startsWith("journey:") ? id.slice(8) : id;
  return (
    list.find((w) => w.ref === id) ??
    (n ? list.find((w) => w.xano_site_id === Number(n)) : undefined) ??
    list.find((w) => !!w.journey_id && w.journey_id === journey) ??
    list.find((w) => w.task_site_ref === id || w.analytics_site_id === id || w.replay_site_id === id || w.journey_site_id === id) ??
    list.find((w) => !!w.domain && (w.domain.toLowerCase() === lower || `www.${w.domain.toLowerCase()}` === lower)) ??
    null
  );
}

/* ───────────────────────────── what the header shows ───────────────────────────── */

export interface NavViewLink { id: string; label: string; href: string }
export interface NavTabLink { id: string; label: string; href: string; views: NavViewLink[]; hint?: string }

export const sectionOf = (id: SectionId) => SECTIONS.find((s) => s.id === id)!;

/** Does this site have what the view needs? Unknown capabilities (a provisional site) never hide a view. */
export const siteHas = (site: NavSite, needs: readonly string[] | undefined) => !needs || !site.capabilities || needs.some((c) => site.capabilities!.includes(c));

function viewHref(v: ViewDef, site: NavSite | null, env: NavEnv): string | null {
  if (v.env && !env[v.env]) return null;
  if (site && !siteHas(site, v.needs)) return null;
  if (site) return v.site?.(site) ?? v.any ?? null;
  return v.all ?? v.any ?? null;
}

/** Carry a section's sticky params (the Analytics date range) onto a link. */
function carry(href: string, keep: string[] | undefined, search: string): string {
  if (!keep?.length || !search) return href;
  const from = new URLSearchParams(search);
  const [path, qs = ""] = href.split("?");
  const to = new URLSearchParams(qs);
  let changed = false;
  for (const k of keep) { const v = from.get(k); if (v && !to.has(k)) { to.set(k, v); changed = true; } }
  return changed ? `${path}?${to}` : href;
}

/** The tabs (and each tab's views) that have a screen for this scope. A tab with no screen is left out. */
export function sectionTabs(section: SectionId, site: NavSite | null, env: NavEnv = {}, search = ""): NavTabLink[] {
  const def = sectionOf(section);
  const out: NavTabLink[] = [];
  for (const t of def.tabs) {
    const views = t.views.flatMap((v) => { const href = viewHref(v, site, env); return href ? [{ id: v.id, label: v.label, href: carry(href, def.keep, search) }] : []; });
    if (views.length) out.push({ id: t.id, label: t.label, href: views[0]!.href, views, ...(t.hint ? { hint: t.hint } : {}) });
  }
  return out;
}

/** Where a section's rail link goes for this scope: its first tab that has a screen. */
export function sectionHref(section: SectionId, site: NavSite | null, env: NavEnv = {}): string | null {
  return sectionTabs(section, site, env)[0]?.href ?? null;
}

/**
 * Where to go when the scope changes on this address: the same tab and view for the new scope when it has a screen,
 * else the section's first tab that has one, else Home.
 */
export function scopeTarget(at: Pick<Resolved, "section" | "tab" | "view"> | null, site: NavSite | null, env: NavEnv = {}, search = ""): string {
  const home = sectionHref("home", site, env) ?? "/sites";
  if (!at) return home;
  const tabs = sectionTabs(at.section, site, env, search);
  const tab = tabs.find((t) => t.id === at.tab);
  if (tab) return (at.view && tab.views.find((v) => v.id === at.view)?.href) || tab.href;
  return tabs[0]?.href ?? home;
}

/** True when this section reads a site at all (Home to Settings). Connections and More tools are account-wide. */
export const isScopedSection = (id: SectionId) => sectionOf(id).place === "main";

/* ───────────────────────────── the switcher's workspace list ───────────────────────────── */

/** Workspaces made by test runs (`__scratch_member_link_…`, `__scratch_activation_…`, `__test…`). */
export const isScratchWorkspace = (name: string) => /^__(scratch|test)/i.test(name.trim());
/**
 * The workspaces the switcher lists: scratch and test workspaces stay out. One that is the current scope (reached by
 * a link) stays listed, so the switcher never names a workspace it does not show.
 */
export function switcherWorkspaces<T extends { id: string; name: string }>(list: readonly T[], current: string | null): T[] {
  return list.filter((w) => !isScratchWorkspace(w.name) || w.id === current);
}
